
import { supabase } from '@/integrations/supabase/client';

interface Location {
  id: string;
  name: string;
  county: string;
  population: number;
  coordinates: string;
  historical_fact: string;
  key_search_terms: string;
}

export const replaceLocationVariables = (content: string, location: Location | null) => {
  if (!location) return content;

  // Determine climate based on location (simplified logic)
  const getLocationClimate = (name: string) => {
    if (name.toLowerCase().includes('florida') || name.toLowerCase().includes('miami')) {
      return 'humid subtropical climate with heavy rainfall and hurricane risks';
    }
    if (name.toLowerCase().includes('texas') || name.toLowerCase().includes('austin')) {
      return 'hot summers and mild winters with occasional severe weather';
    }
    if (name.toLowerCase().includes('california') || name.toLowerCase().includes('san')) {
      return 'Mediterranean climate with dry summers and mild, wet winters';
    }
    return 'varied seasonal weather patterns requiring weather-resistant roofing';
  };

  let replacedText = content;

  // Replace location-specific variables - expanded to cover all variations
  const locationMappings = {
    '{locationName}': location.name,
    '{locationname}': location.name,
    '{Name}': location.name,
    '{name}': location.name,
    '{locationCounty}': location.county,
    '{locationcounty}': location.county,
    '{County}': location.county,
    '{county}': location.county,
    '{locationDescription}': location.historical_fact || `the ${location.name} area`,
    '{locationdescription}': location.historical_fact || `the ${location.name} area`,
    '{historical_fact}': location.historical_fact || `This historic community has a rich heritage dating back generations.`,
    '{locationClimate}': getLocationClimate(location.name),
    '{locationclimate}': getLocationClimate(location.name),
    '{locationPopulation}': location.population?.toString() || 'local community',
    '{locationpopulation}': location.population?.toString() || 'local community',
    '{Population}': location.population?.toString() || 'local community',
    '{population}': location.population?.toString() || 'local community',
    '{locationKeyTerms}': location.key_search_terms || `${location.name} roofing, ${location.county} roofing services`,
    '{locationkeyterms}': location.key_search_terms || `${location.name} roofing, ${location.county} roofing services`,
    '{key_search_terms}': location.key_search_terms || `${location.name} area`,
    '{coordinates}': location.coordinates,
  };

  Object.entries(locationMappings).forEach(([placeholder, value]) => {
    const regex = new RegExp(placeholder.replace('{', '\\{').replace('}', '\\}'), 'g');
    replacedText = replacedText.replace(regex, value);
  });

  // Add business information
  const businessMappings = {
    '{businessPhone}': '(252) 302-2003',
    '{businessEmail}': 'claims@carolinaTrophyRoofs.com',
    '{businessAddress}': '104 S. Business Ct., Rocky Mount, NC 27804',
    '{businessName}': 'Carolina Trophy Roofs',
    '{businessWebsite}': 'CarolinaTrophyRoofs.com',
    '{locationPhone}': '(252) 371-2266'
  };

  Object.entries(businessMappings).forEach(([placeholder, value]) => {
    const regex = new RegExp(placeholder.replace('{', '\\{').replace('}', '\\}'), 'g');
    replacedText = replacedText.replace(regex, value);
  });

  // Add current date
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  replacedText = replacedText.replace(/{current_date}/g, currentDate);

  return replacedText;
};

export const fetchLocationByName = async (locationName: string): Promise<Location | null> => {
  try {
    const { data, error } = await supabase
      .from('service_locations')
      .select('*')
      .ilike('name', `%${locationName}%`)
      .limit(1)
      .single();

    if (error || !data) return null;
    return data as Location;
  } catch (error) {
    console.error('Error fetching location:', error);
    return null;
  }
};
